import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { domainsAPI } from '@hpanel/web-common/api/client'
import type { Domain } from '@hpanel/web-common/api/types'
import { PhpIniEditor } from '../../components/client/PhpIniEditor'

/**
 * Tenant PHP settings page.
 *
 * Pick a domain → edit its per-container php.ini drop-in. The editor
 * shows the operator's global defaults (read-only) above the tenant's
 * own overrides so tenants can see what they're inheriting and what
 * they're changing.
 *
 * Mirrors `DnsEditor` (the established pattern for tenant-scoped
 * settings pages): list domains, pick one, render the editor.
 */
export const PhpSettings: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedId = searchParams.get('domain')
  const [domains, setDomains] = useState<Domain[]>([])
  const [domainId, setDomainId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const r = await domainsAPI.list()
        if (cancelled) return
        setDomains(r.domains)
        const selected = requestedId === null ? r.domains[0] : r.domains.find(d => String(d.id) === requestedId)
        setDomainId(selected?.id ?? null)
        setError(!selected && requestedId !== null ? 'Requested domain is unavailable. Select another domain.' : null)
      } catch (e: unknown) {
        if (cancelled) return
        console.error('domainsAPI.list failed', e)
        setError(e instanceof Error ? e.message : 'Failed to load domains')
      }
    })()
    return () => { cancelled = true }
  }, [requestedId])

  return (
    <div data-testid="php-settings-page" className="space-y-6">
      <div>
        <h1
          data-testid="page-title"
          className="text-3xl font-bold text-gray-900 dark:text-gray-100"
        >
          PHP
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Tune PHP settings for your domain. The operator's global
          defaults apply first; your overrides win where both set the
          same key.
        </p>
      </div>

      {error && (
        <div
          data-testid="php-settings-page-error"
          role="alert"
          className="bg-red-50 dark:bg-red-900/30 border border-red-300 text-red-800 dark:text-red-200 rounded p-3 text-sm"
        >
          {error}
        </div>
      )}

      {domains.length === 0 ? (
        <p className="text-gray-600 dark:text-gray-400">
          You don't own any domains yet. Add one in <strong>Domains</strong>.
        </p>
      ) : (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-4">
          <div>
            <label
              htmlFor="php-settings-domain"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Domain
            </label>
            <select
              id="php-settings-domain"
              data-testid="php-settings-domain-select"
              value={domainId ?? ''}
              onChange={(e) => setSearchParams({ domain: e.target.value })}
              className="input mt-1"
            >
              <option value="" disabled>Select a domain</option>
            {domains.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          {domainId !== null && <PhpIniEditor key={domainId} domainId={domainId} />}
        </div>
      )}
    </div>
  )
}
